import { Injectable } from "@angular/core";
import { EnrollPayloadSaveRequest} from "src/app/angular-elements/shared/interfaces/enrollment-submission.interface";
import { Form, FormGroup } from '@angular/forms';
import { Email } from "../../interfaces/email.interface";
import { ApplicationRecord } from '../../interfaces/application-record.interface';
import { EntitlementsHelperService, LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure } from '@ptl/account-management';
import { INITIAL_STATE as INITIAL_STATE_IndividualMarket } from '../../../form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/reducers/individual-market.reducer';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { PtlSessionService } from '../../../shared/components/ptl-session-loader/services/ptl-session.service';
import { INITIAL_STATE as INITIAL_STATE_EnrollmentApp } from '../../../../shared/reducers/enrollmentApp.reducer'
import { INITIAL_STATE as INITIAL_STATE_SpecialEnrollmentCircumstance } from '../../../form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/reducers/special-enrollment-circumstance.reducer';
import { INITIAL_STATE as INITIAL_STATE_LifeEvent } from '../../../shared/components/configs/reducers/life-event.reducer';
import { IndividualMarketSaveQuoteUtilityService } from "./individual-market-save-quote-utility.service";
import { SAVE_QUOTE_EXACT_TARGET_EMAILTYPEPREFERENCE, SAVE_QUOTE_CARD_TYPE } from "../interfaces/saved-quotes.input.interface";
import { SAVED_QUOTES_EXACT_TARGET_TEMPLATES } from "../interfaces/saved-quotes.exact-target-templates.enum";
import { SaveQuoteReturnUrlService } from "./save-quote-return-url.service";
import { REDUCER_TYPES } from "@ptl/state";
import { ExactTargetResponse, SaveQuoteDataSendReferralFormState } from "../interfaces/save-quote-email-referral.interface";
import { UserInfoService } from "src/app/angular-elements/shared/services/user-info.service";
import { AssignReferralService } from "./assign-referral.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { RetrieveApp } from "src/app/shared/interfaces/application-retrieval/retrieveApp.interface";
import { HttpErrorResponse } from "@angular/common/http";

@Injectable({
  providedIn: 'root'
})
export class SaveQuoteEmailReferralService{

  constructor(
    private reducerUtilityService: ReducerUtilityService,
    private ptlSessionService: PtlSessionService,
    private entitlementsHelperService: EntitlementsHelperService,
    private saveQuoteReturnUrlService: SaveQuoteReturnUrlService,
    private individualMarketSaveQuoteUtilityService: IndividualMarketSaveQuoteUtilityService,
    private userInfoService: UserInfoService,
    private assignReferralService: AssignReferralService,
    private matSnackBar: MatSnackBar
  ){}

  
  emailTriggerIsPending: boolean = false;
  consumerDocumentUploadReturnUrl: string;
  quoteType: SAVE_QUOTE_CARD_TYPE;
  formValuesAsJson: SaveQuoteDataSendReferralFormState;
  applicationRecord: ApplicationRecord;

  async sendEmail(emailForm: FormGroup, applicationRecord: ApplicationRecord, quoteType: SAVE_QUOTE_CARD_TYPE, consumerDocumentUploadReturnUrl: string){  
    this.quoteType = quoteType;
    this.consumerDocumentUploadReturnUrl = consumerDocumentUploadReturnUrl;
    this.applicationRecord = applicationRecord;
    const saveQuoteEmailPayload = await this.getSaveQuoteEmailPayload(emailForm);

    const exactTargetResponse: ExactTargetResponse = await this.userInfoService.sendReferralEmail(saveQuoteEmailPayload)
    await this.assignReferralService.assignReferralId(exactTargetResponse, applicationRecord);
  }

  private async getPlanDataForEmail(sendReferralState: SaveQuoteDataSendReferralFormState): Promise<Email> {
    const response: RetrieveApp | HttpErrorResponse = await this.individualMarketSaveQuoteUtilityService.retrieveApp(this.applicationRecord);

    if(response instanceof HttpErrorResponse){
      console.error(
        `No Application Found!
        Application Information: ${this.applicationRecord}`
      );
    }

    const saveQuoteEnrollmentObj: EnrollPayloadSaveRequest = (response as RetrieveApp).enrollementObject as EnrollPayloadSaveRequest;
    const planState = saveQuoteEnrollmentObj.saveQuoteData.state.componentStates.Plans;
    const planDetailsState = saveQuoteEnrollmentObj.saveQuoteData.state.componentStates.PlanDetails;
    const zipCountyState = saveQuoteEnrollmentObj.saveQuoteData.state.componentStates.ZipCounty;
    const individualMarketState = saveQuoteEnrollmentObj.saveQuoteData.state.payloadStates.individualMarket || INITIAL_STATE_IndividualMarket;
    const effectiveDateState = saveQuoteEnrollmentObj.saveQuoteData.state.appStates.effectiveDate;
    const enrollmentState = saveQuoteEnrollmentObj.saveQuoteData.state.componentStates.enrollmentApp || Object.assign({},INITIAL_STATE_EnrollmentApp,{ appId: this.applicationRecord.applicationTrackingId });
    const specialEnrollmentCircumstanceState = saveQuoteEnrollmentObj.saveQuoteData.state.payloadStates['special-enrollment-circumstance'] || INITIAL_STATE_SpecialEnrollmentCircumstance;
    const lifeEventDocumentUploadState = saveQuoteEnrollmentObj.saveQuoteData.state.componentStates.LifeEvent || INITIAL_STATE_LifeEvent;

    const year = new Date(individualMarketState.ratingInfo.effectiveDate).getFullYear().toString();

    /**
     * returnUrl will either be a new page ( from /enroll/application/special-enrollment-online to /prompt/document-upload/begin/special-enrollment-online, for example )
     * or
     * returnUrl will be consumer version of producer page ( from /individual/new-business/off-exchange/enroll/application/special-enrollment-online to /enroll/application/special-enrollment-online, for example )
     */
    const returnUrl = this.saveQuoteReturnUrlService.transformToConsumerReturnUrl(this.saveQuoteReturnUrlService.getConsumerReferralReturnUrl(saveQuoteEnrollmentObj.saveQuoteData, this.quoteType, this.consumerDocumentUploadReturnUrl));

    const saveQuoteData = this.individualMarketSaveQuoteUtilityService.updateSaveQuoteMetaData((response as RetrieveApp), this.quoteType, this.applicationRecord);
    
    const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const selectedAgency = await this.entitlementsHelperService.getSelectedAgencyPromise();

    /** @TODO: agencyId and agentId are swapped for MN */
    const capturedState = this.ptlSessionService.captureIndividualSessionFromState({
      agentId: selectedAgency.agentACSId,
      agencyId: selectedAgency.agencyId,
      agentName: {
        firstName: loginInfo.basicUserInfo.firstName,
        lastName: loginInfo.basicUserInfo.lastName
      },
      planState: planState,
      planDetailsState: planDetailsState,
      individualMarketState: individualMarketState,
      enrollmentState: enrollmentState,
      specialEnrollmentCircumstanceState: specialEnrollmentCircumstanceState,
      effectiveDateState: effectiveDateState,
      zipCountyState: zipCountyState,
      lifeEventDocumentUploadState: lifeEventDocumentUploadState,
      location: returnUrl,
      saveQuoteName: this.applicationRecord.statusDescription,
      saveQuoteData: saveQuoteData
    });


    const exactTargetTemplate = (() => {
      switch (this.quoteType) {
        case SAVE_QUOTE_CARD_TYPE.DEFAULT_QUOTE_REFERRAL:
          return SAVED_QUOTES_EXACT_TARGET_TEMPLATES.PRODUCER_SHARED_MEDICARE_BLANK;
        case SAVE_QUOTE_CARD_TYPE.DOCUMENT_UPLOAD_REQUEST:
          return SAVED_QUOTES_EXACT_TARGET_TEMPLATES.PRODUCER_DOCUMENT_UPLOAD_REQUEST;
        default:
          return SAVED_QUOTES_EXACT_TARGET_TEMPLATES.PRODUCER_SHARED_MEDICARE_BLANK;
      }
    })();

    const emailTypePreference  = (() => {
      switch (this.quoteType) {
        case SAVE_QUOTE_CARD_TYPE.DEFAULT_QUOTE_REFERRAL:
          return SAVE_QUOTE_EXACT_TARGET_EMAILTYPEPREFERENCE.EMAIL_REFERRAL;
        case SAVE_QUOTE_CARD_TYPE.DOCUMENT_UPLOAD_REQUEST:
          return SAVE_QUOTE_EXACT_TARGET_EMAILTYPEPREFERENCE.DOCUMENT_UPLOAD_REQUEST;
        default:
          return SAVE_QUOTE_EXACT_TARGET_EMAILTYPEPREFERENCE.EMAIL_REFERRAL;
      }
    })();

    let emailPayload: Email = {
      member: false,
      deliveryRecipient: sendReferralState.customerEmailAddress,
      deliveryMethod: 'Email',
      /**
       * This template name was created on the backend for send referrals when only Medicare was using it
       * It's not an accurately descriptive name but backend requires us to use this, then constructs template based off of region provided 
       */
      templateName: exactTargetTemplate,
      memberProfileId: '',
      groupNumber: '',
      umi: '',
      firstName: '',
      lastName: '',
      dob: '',
      etAttributes: [
        {
          name: 'agentCode',
          value: loginInfo.userInfo.npnNo || selectedAgency.npnNo
        },
        {
          name: 'EmailTypePreference',
          value: emailTypePreference
        },
        {
          name: 'agencyNumber',
          value: selectedAgency.agencyId
        },
        {
          name: 'Agency_Name',
          value: selectedAgency.agencyName
        },
        {
          name: 'Brand_Name',
          value: zipCountyState.county.region // selectedAgency.region
        },
        {
          name: 'user_ID',
          value: loginInfo.userId
        },
        {
          name: 'year',
          value: year
        },
        {
          name: 'client_Name',
          value: sendReferralState.clientName
        },
        {
          name: 'Agent_Name',
          value: loginInfo.userInfo.firstName + ' ' + loginInfo.userInfo.lastName
        },
        {
          name: 'Agent_Phone',
          value: loginInfo.userInfo.telephone
        },
        {
          name: 'agentNotes',
          value: sendReferralState?.agentNotes ? `Agent Notes: ${sendReferralState.agentNotes}` : null
        },
        {
          name: "planSessionPayload",
          value: JSON.stringify(capturedState)
        }
      ]
    }

    return emailPayload;
  }

  async getSaveQuoteEmailPayload( emailForm: FormGroup ): Promise<Email> {
    //assign global values so we don't have to pass between service functions

    if (emailForm.controls.customerEmailAddress.value) {
      const saveQuoteDataObj = {
        customerEmailAddress: emailForm.controls.customerEmailAddress.value,
        clientName: emailForm?.controls?.clientName?.value ?? '',
        agentNotes: emailForm?.controls?.agentNotes?.value ?? ''
      }
      this.emailTriggerIsPending = true;
      const payload: Email = await this.getPlanDataForEmail(saveQuoteDataObj);   
      return payload;
    }
  }

  isEligibleToSendQuotes() {
    const userInfoState: UserInfoStateStructure = this.reducerUtilityService.getGlobalStateItem(`${REDUCER_TYPES.APPLICATION_STATE}.${LOGIN_INFO_REDUCER_NAME}`);
    const npn = userInfoState.userInfo.npnNo;

    if (this.quoteType === SAVE_QUOTE_CARD_TYPE.DOCUMENT_UPLOAD_REQUEST) {
      return true;
    }
    
    if (userInfoState.userInfo.role.toUpperCase() === 'INTERNAL' || !npn) {
      return false;
    } else {
      return true;
    }
  }
  
}